import { Link } from "react-router-dom";
import {
  ArrowRight, Landmark, Layers, ShieldCheck, Globe2, Eye, Scale, Building2,
  FileSearch, Banknote, MapPin, Users2, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/lib/seo";
import {
  Reveal, SectionHeading, CTASection, ComplianceNote, CheckItem, FAQSection, ProcessTimeline, stagger, fadeUp,
} from "@/components/shared/Shared";
import { motion } from "framer-motion";

const IMG_HERO = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop";

export default function FamilyOffice() {
  const aufgaben = [
    {
      icon: Layers,
      title: "Struktur",
      text: "Aufbau internationaler Vermögensstrukturen – abgestimmt auf Ihre Ziele, Ihre Familie und Ihre steuerliche Situation. Diversifikation über Anlageklassen und Rechtsordnungen statt Konzentration an einem Ort.",
    },
    {
      icon: Landmark,
      title: "Verwaltung",
      text: "Professionelle Vermögensverwaltung über regulierte Strukturen in der Schweiz. Investiert wird in mehrere Anlageklassen – unter anderem Unternehmensanleihen, Immobilienbestände, Versicherungsbestände und Unternehmensbeteiligungen.",
    },
    {
      icon: ShieldCheck,
      title: "Schutz",
      text: "Ihr Kapital wird als Sondervermögen geführt und liegt zu keinem Zeitpunkt bei Eleven. Beratung, Verwaltung und Verwahrung sind konsequent getrennt – das Vermögen bleibt in Ihrem eigenen Depot einsehbar.",
    },
  ];

  const loesungen = [
    {
      title: "Internationale Vermögensstruktur",
      badge: "Individuell",
      text: "Aufbau einer auf Ihre Situation zugeschnittenen internationalen Struktur – in Zusammenarbeit mit Ihrem Steuerberater, Notaren und externen Spezialisten.",
      punkte: [
        "Diversifikation über mehrere Rechtsordnungen",
        "Abstimmung mit Steuerberater und Notar",
        "Einstieg je nach Struktur ab 25.000–50.000 €",
      ],
    },
    {
      title: "Schweizer Vermögensverwaltung",
      badge: "Ab 250.000 €",
      text: "Vermögensverwaltung über ein eigenes Wertpapier mit ISIN. Emission und Administration erfolgen über die GenTwo AG in Zürich – Ihr Kapital wird als Sondervermögen geführt.",
      punkte: [
        "Eigenes Wertpapier mit ISIN, im Depot sichtbar",
        "Mehrere Anlageklassen, professionell verwaltet",
        "Anlagehorizont ab rund 3,5 Jahren",
      ],
    },
    {
      title: "Versicherungsmantel Liechtenstein",
      badge: "Ab 100.000 €",
      text: "Vermögensverwaltung im Rahmen einer Liechtensteiner Versicherungslösung – mit Zugang zu über 6.000 Fonds und ETFs und planbaren Auszahlungswegen.",
      punkte: [
        "Kapital als Sondervermögen geführt",
        "Zugang zu über 6.000 Fonds und ETFs",
        "Planbare Auszahlung: Teilentnahmen, laufende Rente, Regelung im Erbfall",
      ],
    },
  ];

  const standorte = [
    { ort: "Stuttgart", rolle: "Sitz der Gruppe – Beratung und persönliche Betreuung" },
    { ort: "Schweiz", rolle: "Vermögensverwaltung und Emission über die GenTwo AG, Zürich" },
    { ort: "Liechtenstein", rolle: "Versicherungslösungen und Vermögensschutz" },
    { ort: "Luxemburg", rolle: "Fondsstrukturen" },
  ];

  const ablauf = [
    { title: "Strukturanalyse", text: "Kostenfreies Gespräch von rund 45 Minuten: Wir analysieren Ihre bestehende Vermögensstruktur – bevor über Lösungen gesprochen wird." },
    { title: "Vorschlag", text: "Sie erhalten einen nachvollziehbaren Struktur- und Anlagevorschlag – schriftlich und verständlich dokumentiert." },
    { title: "Prüfung", text: "Sie prüfen den Vorschlag in Ruhe – gerne gemeinsam mit Ihrem Steuerberater, Ihrer Familie oder weiteren Vertrauten." },
    { title: "Umsetzung", text: "Zeichnung und Eröffnung der Struktur. Ihr Kapital überweisen Sie direkt in die Struktur – niemals an Eleven." },
    { title: "Begleitung", text: "Laufende Betreuung und regelmäßiges Reporting – mit einem festen Ansprechpartner über die gesamte Zusammenarbeit." },
  ];

  const risiken = [
    "Kapitalanlagen unterliegen Wertschwankungen. Frühere Entwicklungen sind kein verlässlicher Indikator für die Zukunft – Ergebnisse werden nicht garantiert.",
    "Die Lösungen sind auf einen mittleren bis langen Anlagehorizont ausgelegt. Kurzfristige Verfügbarkeit ist je nach Lösung eingeschränkt.",
    "Für die Lösungen gelten Mindestanlagen und Mindestlaufzeiten – sie eignen sich nicht für jede Vermögenssituation.",
    "Eleven erbringt keine Steuer- oder Rechtsberatung. Steuerliche und rechtliche Fragen werden mit Ihrem Steuerberater bzw. Notar abgestimmt.",
  ];

  const faq = [
    {
      q: "Was passiert mit meinem Kapital, wenn es Eleven eines Tages nicht mehr gibt?",
      a: "Ihr Kapital liegt zu keinem Zeitpunkt bei Eleven. Es wird als Sondervermögen in regulierten Strukturen geführt und ist in Ihrem eigenen Depot bzw. Ihrer Versicherungslösung einsehbar. Beratung, Verwaltung und Verwahrung sind konsequent getrennt.",
    },
    {
      q: "Ab welchem Vermögen ist eine Zusammenarbeit sinnvoll?",
      a: "Der Einstieg in eine internationale Struktur ist je nach Lösung ab etwa 25.000–50.000 € möglich, der Versicherungsmantel ab 100.000 €, die Schweizer Vermögensverwaltung ab 250.000 €. Ob eine Lösung zu Ihrer Situation passt, klären wir in der Strukturanalyse.",
    },
    {
      q: "Wie lange bin ich gebunden?",
      a: "Die Vermögensverwaltung ist auf einen Anlagehorizont ab rund 3,5 Jahren ausgelegt. Auszahlungswege – etwa Teilentnahmen oder eine laufende Rente – werden vorab klar geregelt und dokumentiert.",
    },
    {
      q: "Läuft mein Geld über Eleven?",
      a: "Nein. Sie überweisen Ihr Kapital direkt in die jeweilige Struktur – zum Beispiel auf Ihr Depot oder in Ihre Versicherungslösung. Eleven nimmt keine Kundengelder entgegen.",
    },
    {
      q: "Welche Kosten entstehen?",
      a: "Alle Kosten werden vor der Umsetzung transparent und schriftlich offengelegt – abhängig von der gewählten Lösung. Die Strukturanalyse selbst ist kostenfrei und unverbindlich.",
    },
    {
      q: "Arbeitet Eleven mit meinem Steuerberater zusammen?",
      a: "Ja, ausdrücklich. Internationale Strukturen werden gemeinsam mit Ihrem Steuerberater, Notaren und externen Spezialisten abgestimmt – damit die Lösung zu Ihrer Gesamtsituation passt.",
    },
  ];

  return (
    <div className="bg-white">
      <Seo
        title="Eleven Family Office – Vermögensverwaltung & internationale Vermögensstrukturen"
        description="Analyse der Vermögensstruktur, Vermögensverwaltung in der Schweiz, Versicherungslösungen in Liechtenstein: Eleven Family Office begleitet Unternehmer, Investoren und vermögende Privatpersonen – diskret und langfristig."
      />

      {/* HERO */}
      <section className="bg-[#111318] noise-overlay accent-hairline" data-testid="fo-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div variants={stagger} initial="hidden" animate="show" className="lg:col-span-7">
              <motion.div variants={fadeUp} className="flex items-center gap-3">
                <span className="h-px w-8 bg-[#FF3C20]" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/60">Eleven Family Office</p>
              </motion.div>
              <motion.h1 variants={fadeUp} className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] text-white" data-testid="fo-hero-headline">
                Am Anfang steht nicht das Produkt. Sondern Ihre Struktur.
              </motion.h1>
              <motion.p variants={fadeUp} className="mt-5 text-base md:text-lg text-white/60 leading-relaxed max-w-[60ch]">
                Eleven Family Office verbindet die Analyse Ihrer Vermögensstruktur mit professioneller Verwaltung, internationalen Strukturen und Vermögensschutz – mit einem Ansprechpartner und klar getrennter Verwahrung.
              </motion.p>
              <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-[6px] h-12 px-6 bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold transition-colors duration-200" data-testid="fo-hero-primary-cta">
                  <Link to="/anfrage/strukturanalyse">Strukturanalyse anfragen <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-[6px] h-12 px-6 border-white/30 text-white bg-transparent hover:bg-white/5 hover:text-white hover:border-white transition-colors duration-200" data-testid="fo-hero-secondary-cta">
                  <Link to="/termin">Persönliches Erstgespräch</Link>
                </Button>
              </motion.div>
            </motion.div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative rounded-[8px] overflow-hidden ring-1 ring-white/10 ">
                <img src={IMG_HERO} alt="Institutionelle Architektur – Eleven Family Office" className="w-full h-[440px] object-cover" loading="eager" decoding="async" data-testid="fo-hero-image" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111318]/60 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUSGANGSLAGE */}
      <section className="bg-white py-14 sm:py-20" data-testid="fo-ausgangslage">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Ausgangslage"
            title="Vermögen entsteht über Jahrzehnte. Seine Struktur entscheidet, ob es bleibt."
            subtitle="Viele Vermögen in Deutschland sind stark konzentriert: Immobilien, Betriebsvermögen und Kontoguthaben – häufig in einer einzigen Rechtsordnung. Das schafft Abhängigkeiten, die sich strukturieren lassen."
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Banknote, title: "Kaufkraft", text: "Unverzinste Liquidität verliert durch Inflation kontinuierlich an Kaufkraft – schleichend, aber dauerhaft. Struktur bedeutet, Kapital bewusst arbeiten zu lassen, statt es unbewusst zu entwerten." },
              { icon: Scale, title: "Konzentration", text: "Wer Vermögen, Einkommen und Eigentum ausschließlich in einem Land hält, bündelt auch alle rechtlichen und wirtschaftlichen Risiken an einem Ort. Diversifikation über Rechtsordnungen verteilt diese Risiken." },
              { icon: Eye, title: "Überblick", text: "Gewachsene Vermögen bestehen oft aus vielen Einzelbausteinen ohne Gesamtbild. Die Strukturanalyse schafft Transparenz – als Grundlage jeder weiteren Entscheidung." },
            ].map((c) => (
              <Reveal key={c.title} className="rounded-[8px] border border-border bg-[#F6F7F8] p-8">
                <div className="h-11 w-11 rounded-[6px] bg-[#111318] flex items-center justify-center">
                  <c.icon className="h-5 w-5 text-[#FF3C20]" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold text-[#111318]">{c.title}</h3>
                <p className="mt-2.5 text-sm text-[#3B4452] leading-relaxed">{c.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DREI AUFGABEN */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20" data-testid="fo-aufgaben">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="Unser Mandat"
            title="Drei Aufgaben. Eine Verantwortung."
            subtitle="Struktur, Verwaltung und Schutz greifen ineinander – koordiniert von einem Ansprechpartner."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {aufgaben.map((a, i) => (
              <Reveal key={a.title} className="rounded-[8px] bg-white border border-border p-8 card-hover" data-testid={`fo-aufgabe-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 rounded-[6px] bg-[#111318] flex items-center justify-center">
                    <a.icon className="h-5 w-5 text-[#FF3C20]" />
                  </div>
                  <span className="font-mono-num text-xs font-semibold text-[#E8241B]">0{i + 1}</span>
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-[#111318]">{a.title}</h3>
                <p className="mt-3 text-sm text-[#3B4452] leading-relaxed">{a.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DREI LÖSUNGEN */}
      <section className="bg-white py-14 sm:py-20" data-testid="fo-loesungen">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Lösungsarchitektur"
            title="Drei Wege – abgestimmt auf Ihre Vermögenssituation"
            subtitle="Welche Lösung passt, ergibt sich aus der Strukturanalyse – nicht umgekehrt. Häufig ist auch eine Kombination sinnvoll."
          />
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {loesungen.map((l, i) => (
              <Reveal key={l.title} className="h-full">
                <div className="h-full rounded-[8px] border border-border bg-white p-8 card-hover flex flex-col" data-testid={`fo-loesung-${i + 1}`}>
                  <Badge variant="outline" className="self-start rounded-[6px] bg-[#FDEFEC] text-[#E8241B] border-[#FF3C20]/50 font-medium">{l.badge}</Badge>
                  <h3 className="mt-4 font-display text-xl font-semibold text-[#111318]">{l.title}</h3>
                  <p className="mt-3 text-sm text-[#3B4452] leading-relaxed">{l.text}</p>
                  <div className="mt-5 space-y-2.5 flex-1">
                    {l.punkte.map((p) => (
                      <CheckItem key={p}>{p}</CheckItem>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 max-w-4xl">
            <ComplianceNote>
              Kapitalanlagen sind mit Risiken verbunden; Wertschwankungen und Verluste sind möglich. Renditeerwartungen, Kosten und Konditionen werden ausschließlich im persönlichen Gespräch nach individueller Prüfung besprochen – nicht pauschal auf dieser Website. Diese Inhalte stellen keine Anlage-, Steuer- oder Rechtsberatung dar.
            </ComplianceNote>
          </div>
        </div>
      </section>

      {/* WO DAS KAPITAL LIEGT */}
      <section className="dark-section noise-overlay py-14 sm:py-20" data-testid="fo-kapital">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <SectionHeading
                dark
                eyebrow="Sicherheit & Transparenz"
                title="Wo Ihr Kapital liegt – und wo nicht"
                subtitle="Die wichtigste Frage zuerst: Ihr Geld fließt niemals an Eleven."
              />
              <div className="mt-8 space-y-5">
                {[
                  { icon: Banknote, title: "Direkter Zahlungsweg", text: "Sie überweisen Ihr Kapital direkt in die jeweilige Struktur – auf Ihr Depot oder in Ihre Versicherungslösung. Eleven nimmt keine Kundengelder entgegen." },
                  { icon: ShieldCheck, title: "Sondervermögen", text: "Ihr Kapital wird als Sondervermögen geführt – getrennt vom Vermögen der beteiligten Gesellschaften." },
                  { icon: Eye, title: "Jederzeit einsehbar", text: "Die Schweizer Vermögensverwaltung erfolgt über ein eigenes Wertpapier mit ISIN – sichtbar in Ihrem eigenen Depot. Emission und Administration übernimmt die GenTwo AG in Zürich." },
                  { icon: Scale, title: "Getrennte Rollen", text: "Beratung (Eleven), Verwaltung und Verwahrung (regulierte Partner) sind konsequent getrennt – niemand vereint alle Rollen auf sich." },
                ].map((p) => (
                  <div key={p.title} className="flex gap-4">
                    <div className="h-10 w-10 rounded-[6px] bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                      <p.icon className="h-4.5 w-4.5 h-5 w-5 text-[#FF3C20]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{p.title}</p>
                      <p className="mt-1 text-sm text-white/55 leading-relaxed">{p.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <SectionHeading
                dark
                eyebrow="Standorte"
                title="International aufgestellt – persönlich betreut"
                subtitle="Beraten wird in Stuttgart. Strukturiert wird dort, wo es für Ihre Ziele sinnvoll ist."
              />
              <div className="mt-8 space-y-3">
                {standorte.map((s) => (
                  <div key={s.ort} className="flex items-start gap-3.5 rounded-[6px] border border-white/10 bg-white/[0.03] p-4" data-testid={`fo-standort-${s.ort.toLowerCase()}`}>
                    <MapPin className="h-4 w-4 text-[#FF3C20] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-white">{s.ort}</p>
                      <p className="text-sm text-white/55">{s.rolle}</p>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-white/40 leading-relaxed pt-2">
                  Situativ werden – abhängig von Zielsetzung und Vermögenssituation – weitere Standorte wie Hongkong, Georgien oder die USA einbezogen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FÜR WEN */}
      <section className="bg-white py-14 sm:py-20" data-testid="fo-zielgruppe">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <SectionHeading
              eyebrow="Für wen wir arbeiten"
              title="Für Menschen, die Vermögen nicht nur besitzen, sondern verantworten"
              subtitle="Eleven Family Office arbeitet für Unternehmer, Investoren und vermögende Privatpersonen – häufig über Generationen hinweg."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Building2, title: "Unternehmer", text: "Trennung von Betriebs- und Privatvermögen, Strukturierung von Verkaufserlösen, Nachfolge." },
                { icon: FileSearch, title: "Investoren", text: "Diversifikation über Anlageklassen und Rechtsordnungen – mit professioneller Verwaltung." },
                { icon: Users2, title: "Familien", text: "Vermögensschutz, planbare Auszahlungswege und klare Regelungen für den Erbfall." },
                { icon: Globe2, title: "International Denkende", text: "Menschen, die ihr Vermögen nicht ausschließlich in einer Rechtsordnung halten möchten." },
              ].map((z) => (
                <Reveal key={z.title} className="rounded-[8px] border border-border bg-[#F6F7F8] p-6">
                  <z.icon className="h-5 w-5 text-[#E8241B]" />
                  <h4 className="mt-3 text-sm font-semibold text-[#111318]">{z.title}</h4>
                  <p className="mt-1.5 text-xs text-[#3B4452] leading-relaxed">{z.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ABLAUF */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20" data-testid="fo-ablauf">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="Ablauf des Mandats"
            title="Von der Analyse zur Struktur – in der Regel 4 bis 6 Wochen"
            subtitle="Jeder Schritt ist dokumentiert. Sie entscheiden in Ihrem Tempo."
          />
          <div className="mt-12">
            <ProcessTimeline steps={ablauf} />
          </div>
        </div>
      </section>

      {/* RISIKEN & GRENZEN */}
      <section className="bg-white py-14 sm:py-20" data-testid="fo-risiken">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <SectionHeading
              eyebrow="Risiken, Voraussetzungen & Grenzen"
              title="Was Sie wissen sollten, bevor Sie starten"
              subtitle="Seriöse Vermögensstrukturierung beginnt mit klaren Grenzen – nicht mit Versprechen."
            />
            <div className="space-y-4">
              {risiken.map((r, i) => (
                <Reveal key={i} className="flex items-start gap-3.5 rounded-[6px] border border-border bg-[#F6F7F8] p-5" data-testid={`fo-risiko-${i + 1}`}>
                  <AlertTriangle className="h-4 w-4 text-[#E8241B] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#2A303A] leading-relaxed">{r}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20" data-testid="fo-faq">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Häufige Fragen" title="Fragen zum Family Office" />
          <div className="mt-10">
            <FAQSection items={faq} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Beginnen Sie mit der Analyse – nicht mit einem Produkt."
        subtitle="In einer kostenfreien Strukturanalyse von rund 45 Minuten verschaffen wir Ihnen ein klares Bild Ihrer Vermögensstruktur – diskret, dokumentiert und unverbindlich."
        primaryLabel="Strukturanalyse anfragen"
        primaryTo="/anfrage/strukturanalyse"
        secondaryLabel="Termin online buchen"
        secondaryTo="/termin"
      />
    </div>
  );
}
